import React from "react";
import { Helmet } from 'react-helmet';

import PricingTable from "./pricingTable";
import FAQ from "./faq";

const Pricing = () => {
  const pricingPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.partneredmg.com/pricing#webpage",
    "url": "https://www.partneredmg.com/pricing",
    "name": "Fractional Project Management Pricing | Partnered Program | PMG",
    "description": "PMG's fractional project management plans start at competitive rates — 30% less than a full-time W-2 hire. Flexible month-to-month or contract options for businesses of all sizes.",
    "isPartOf": { "@id": "https://www.partneredmg.com/#website" },
    "about": { "@id": "https://www.partneredmg.com/#organization" },
    "breadcrumb": { "@id": "https://www.partneredmg.com/pricing#breadcrumb" }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.partneredmg.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pricing",
        "item": "https://www.partneredmg.com/pricing"
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the Partnered Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Partnered Program is a white-label dedicated fractional services program. The program allows organizations to integrate top tier dedicated resources at a fraction of the cost."
        }
      },
      {
        "@type": "Question",
        "name": "How can the Partnered Program benefit my organization?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "By subscribing to the Partnered Program, you'll unlock access to top-tier Practitioners that will supercharge your organization's success. With our help, you can complete projects more efficiently, increase net margin, and propel your business forward."
        }
      },
      {
        "@type": "Question",
        "name": "How does fractional project management pricing compare to hiring full-time?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Partnered Program offers top-tier fractional services with significant cost savings. We are 30% less expensive compared to hiring a W-2 employee. Our commitment to reducing project and operational overhead expenses by an average of 10% further showcases the value of our service."
        }
      },
      {
        "@type": "Question",
        "name": "What types of fractional services does PMG provide?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "By joining the Partnered Program you gain access to a team of highly skilled operators with over 30 years of combined experience. From fractional project management and operations to creative and technical execution, our experts bring the critical thinking and analytical skills necessary to tackle your most complex challenges."
        }
      },
      {
        "@type": "Question",
        "name": "Is there a minimum engagement length?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We have a 2 month minimum commitment because tangible results take more than a few weeks to surface. Two months is when our clients start seeing the ROI and operational impact that made them reach out in the first place. After that it's month to month, or you can save with a 6 or 12 month contract."
        }
      },
      {
        "@type": "Question",
        "name": "Can I upgrade or downgrade my PMG plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The Partnered Program is built to scale with your organization and adjust to current company needs. Clients are able to change their plan before the 1st of each month."
        }
      },
      {
        "@type": "Question",
        "name": "Can my PMG plan be cancelled at any time?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, if you would like to cancel your account please email cancel@partneredmg.com with the associated email on the account. Your account will be canceled at the end of the next billing cycle."
        }
      }
    ]
  };

  return (
    <div>
      <Helmet>
        <title>Fractional Project Management Pricing | Partnered Program | PMG</title>
        <meta name="description" content="PMG's fractional project management plans start at competitive rates — 30% less than a full-time W-2 hire. Flexible month-to-month or contract options for businesses of all sizes." />
        <link rel="canonical" href="https://www.partneredmg.com/pricing" />
        <meta property="og:title" content="Fractional PM Pricing | The Partnered Program | PMG" />
        <meta property="og:description" content="Transparent fractional project management pricing. 30% less than a full-time hire. Month-to-month flexibility or save with a 6 or 12-month contract." />
        <meta property="og:url" content="https://www.partneredmg.com/pricing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(pricingPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="w-full flex flex-col px-6 container mx-auto lg:mt-28 mt-16">
        <div className="text-black text-left flex flex-col gap-6 py-24 px-6">
          <h1 className="text-3xl md:text-5xl pointer-events-none text-blue10">
            Partnered Program
          </h1>
          <h2 className="text-2xl md:text-3xl font-light pointer-events-none text-blue20">
            Fractional Rates - Maximum Results
          </h2>
          <p className="text-base md:text-xl leading-normal pointer-events-none text-grey45">
          We match the right expertise to your specific needs across project management, operations, creative, and technical execution. <br/>
          Every engagement is tailored to your unique needs, at fractional rates that make sense for your business. 
          </p>
        </div>
        <PricingTable />
      </div>
      <FAQ />
    </div>
  );
};

export default Pricing;
